import { AUTH_URL } from '../constants';
import { APIRequest, APIResponse, HTTPClient, RequestType } from '../services/http_client.service';
import { SignInType, User } from '../types/user';

const client = new HTTPClient();

export interface UserResponse {
    access_token?: string;
    expires_in?: number;
    token_type?: string;
    message?: string;
}

export interface UsersList {
    users?: {
        data: Array<User>;
    };
    total?: number;
    error?: string;
}

export interface UsersFilters {
    profile: number;
    active: number;
    page?: number;
    limit?: number;
}

export interface UserForm {
    name: string;
    profile: number;
    password?: string;
    active: number;
    email: string;
}

export interface UserFormResponse {
    success: boolean;
    error?: string;
}

class UserController {
    static async create(data: UserForm) {
        const request = new APIRequest({
            type: RequestType.POST,
            fullUrl: `${AUTH_URL}/users`,
            body: JSON.stringify(data)
        });

        const response = await client.post(request);

        if (!response.success) {
            return new APIResponse({
                success: false,
                statusCode: response.statusCode,
                errorMessage: response.errorMessage
            });
        }

        return response;
    }

    static async update(userId: number, data: UserForm) {
        const request = new APIRequest({
            type: RequestType.PUT,
            fullUrl: `${AUTH_URL}/users/${userId}`,
            body: JSON.stringify(data)
        });

        const response = await client.put(request);

        if (!response.success) {
            return {
                error: response.errorMessage
            };
        }

        return response.result as unknown as UserFormResponse;
    }

    static async find(userId: number) {
        const request = new APIRequest({
            type: RequestType.GET,
            fullUrl: `${AUTH_URL}/users/${userId}`
        });

        const response = await client.get(request);

        if (!response.success) {
            return {
                error: response.errorMessage
            };
        }

        return response.result as unknown as UsersList;
    }

    static async delete(userId: number) {
        const request = new APIRequest({
            type: RequestType.DELETE,
            fullUrl: `${AUTH_URL}/users/${userId}`
        });

        const response = await client.delete(request);

        if (!response.success) {
            return {
                error: response.errorMessage
            };
        }

        return response.result as unknown as UsersList;
    }

    static async getAll({ profile, active, page, limit }: UsersFilters) {
        const request = new APIRequest({
            type: RequestType.GET,
            fullUrl: `${AUTH_URL}/users?profile=${profile}&active=${active}&page=${page}&limit=${limit}`
        });

        const response = await client.get(request);

        if (!response.success) {
            return {
                error: response.errorMessage
            };
        }

        return response.result as unknown as UsersList;
    }

    static async signIn(data: SignInType): Promise<APIResponse> {
        const request = new APIRequest({
            type: RequestType.POST,
            fullUrl: `${AUTH_URL}/signin`,
            body: JSON.stringify(data)
        });

        const response = await client.post(request);

        if (!response.success) {
            return new APIResponse({
                success: false,
                errorMessage: response.errorMessage
            });
        }

        return response;
    }

    static async refreshToken(): Promise<APIResponse> {
        const request = new APIRequest({
            type: RequestType.POST,
            fullUrl: `${AUTH_URL}/refresh`,
            isAuthenticated: true
        });

        const response = await client.post(request);

        if (!response.success) {
            return new APIResponse({
                success: false,
                errorMessage: response.errorMessage
            });
        }

        return response;
    }
}

export default UserController;
